import axios from 'axios';

// Base URL for pricely-services, e.g. https://pricely-api.danubehome.com or https://pricely-api.danubehome.com/
const PRICELY_API_BASE = (process.env.SELLER_HUB_PRICELY_API || '').replace(/\/+$/, '');
// Shared static key that proves the request came from mp-sellerhub-ui.
// This is NOT the seller's Firebase token - pricely-services does not verify
// the caller's identity beyond this key (see x-seller-hub-api-key header below).
const PRICELY_API_KEY = process.env.SELLER_HUB_PRICELY_API_KEY || '';

const pricelyClient = axios.create({
  baseURL: `${PRICELY_API_BASE}/rest/v1/seller-promotions/`,
});

pricelyClient.interceptors.request.use((config) => ({
  ...config,
  headers: {
    ...config.headers,
    'x-seller-hub-api-key': PRICELY_API_KEY,
  },
}));

/**
 * GET rest/v1/seller-promotions/seller-hub/list
 * Lists the currently open seller promotions.
 * Passing `sellerEmail` makes each item carry a `sellerProductCounts`
 * `{ approved, rejected, total }` for the products that seller has uploaded.
 * @param {{page?: number, limit?: number, search?: string, startDate?: string, endDate?: string, store?: string, country?: string, sellerEmail?: string}} params
 */
export const getSellerHubPromotions = (params = {}) => pricelyClient.get('seller-hub/list', { params });

/**
 * GET rest/v1/seller-promotions/seller-hub/products
 * Lists the caller's own products for one promotion, paginated, returning
 * `{ items, pageInfo }`. `status` narrows it to 'Approved' or 'Rejected'.
 * @param {{promotionId: string, sellerEmail: string, status?: string, page?: number, limit?: number}} params
 */
export const getSellerHubPromotionProducts = (params = {}) => pricelyClient.get('seller-hub/products', { params });

/**
 * POST rest/v1/seller-promotions/seller-hub/delete-products
 * Deletes the caller's own products from a promotion, returning `{ deletedCount }`.
 * Rejected server-side once the promotion's registration has closed.
 * @param {{promotionId: string, sellerEmail: string, skus: string[]}} payload
 */
export const deleteSellerHubPromotionProducts = (payload = {}) => pricelyClient.post('seller-hub/delete-products', payload);

/**
 * GET rest/v1/seller-promotions/seller-hub/export-products
 * Exports the caller's own products for one promotion as XLSX, returning
 * `{ url, count }`. `status` narrows the export to 'Approved' or 'Rejected'.
 * @param {{promotionId: string, sellerEmail: string, status?: string}} params
 */
export const exportSellerHubPromotionProducts = (params = {}) => pricelyClient.get('seller-hub/export-products', { params });

/**
 * POST rest/v1/seller-promotions/product-promotion-step<stepNumber>
 * Generic helper for the step1-step11 multipart product-upload pipeline.
 * @param {number} stepNumber - 1 through 11
 * @param {FormData} formData
 */
export const postPricelyStep = (stepNumber, formData) =>
  pricelyClient.post(`product-promotion-step${stepNumber}`, formData, {
    headers: { 'content-type': 'multipart/form-data' },
  });

export default pricelyClient;
